import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../layouts/Header';
import {
    Badge, Button, Col, Dropdown, DropdownButton,
    Form, InputGroup, Nav, Row, Spinner, Table
} from 'react-bootstrap';
import { ArrowLeft, FileEarmarkSpreadsheet, Search } from 'react-bootstrap-icons';
import DatePicker from 'react-datepicker';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { useNavigate } from 'react-router-dom';
import OutletSelect from '../../components/OutletSelect';
import { MAX_DATE_RANGE_DAYS, DATE_RANGE_ERROR_MESSAGE } from '../../constants/dateConstants';

/* ─────────────────────────────────────────────────────────────────────────────
   Small reusable helpers
───────────────────────────────────────────────────────────────────────────── */
const SortIcon = ({ sortConfig, column }) => {
    if (sortConfig.key !== column)
        return <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 11 }}>⇅</span>;
    return (
        <span style={{ marginLeft: 4, fontSize: 11, color: '#0d6efd' }}>
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
        </span>
    );
};

const useSortedData = (data, sortConfig) =>
    useMemo(() => {
        if (!sortConfig.key) return data;
        return [...data].sort((a, b) => {
            const aVal = a[sortConfig.key] ?? 0;
            const bVal = b[sortConfig.key] ?? 0;
            const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [data, sortConfig]);

const SORTABLE_COLS = [
    { key: 'outletName', label: 'Outlet', width: 110 },
    { key: 'productName', label: 'Category & Product', width: null },
    { key: 'addonTitle', label: 'Addon Group', width: null },
    { key: null, label: 'Addon Values', width: null },        // not sortable
    { key: null, label: 'Sales Count', width: null, center: true },
    { key: 'totalAddonSalesCount', label: 'Total Sales Count', width: null, center: true },
    { key: 'totalDiscount', label: 'Discount', width: 120, end: true },
    { key: 'totalTaxable', label: 'Taxable', width: 120, end: true },
    { key: 'totalTaxAmount', label: 'Tax', width: 110, end: true },
    { key: 'totalGrandTotal', label: 'Grand Total', width: 120, end: true },
];

/* ─────────────────────────────────────────────────────────────────────────────
   AddonTable — renders one table of addon rows + totals row
───────────────────────────────────────────────────────────────────────────── */
const AddonTable = ({ rows, loading, searchText, onSearchChange, onSearchSubmit }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [localSearch, setLocalSearch] = useState(searchText ?? '');

    const handleSort = (key) => {
        if (!key) return;
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedRows = useSortedData(rows, sortConfig);

    const totals = useMemo(() => rows.reduce((acc, r) => ({
        totalAddonSalesCount: acc.totalAddonSalesCount + (r.totalAddonSalesCount || 0),
        totalDiscount: acc.totalDiscount + (r.totalDiscount || 0),
        totalTaxable: acc.totalTaxable + (r.totalTaxable || 0),
        totalTaxAmount: acc.totalTaxAmount + (r.totalTaxAmount || 0),
        totalGrandTotal: acc.totalGrandTotal + (r.totalGrandTotal || 0),
    }), { totalAddonSalesCount: 0, totalDiscount: 0, totalTaxable: 0, totalTaxAmount: 0, totalGrandTotal: 0 }), [rows]);

    return (
        <div className="table-responsive border rounded-3">
            <Table striped bordered hover className="mb-0">
                <thead className="bg-light">
                    <tr>
                        <th width={50}>#</th>
                        {SORTABLE_COLS.map((col, i) => (
                            <th
                                key={i}
                                width={col.width ?? undefined}
                                className={`${col.center ? 'text-center' : ''} ${col.end ? 'text-end' : ''}`}
                                style={col.key ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : {}}
                                onClick={() => handleSort(col.key)}
                            >
                                {col.label}
                                {col.key && <SortIcon sortConfig={sortConfig} column={col.key} />}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={11} className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 mb-0">Loading data…</p>
                            </td>
                        </tr>
                    ) : sortedRows.length > 0 ? (
                        <>
                            {sortedRows.map((addon, idx) => (
                                <tr key={addon._id ?? idx}>
                                    <td className="text-muted">{idx + 1}</td>
                                    <td>{addon.outletName}</td>
                                    <td>
                                        <div className="text-dark">{addon.productName}</div>
                                        <div className="fw-semibold text-primary">{addon.categoryName}</div>
                                    </td>
                                    <td className="fw-semibold text-muted">{addon.addonTitle || '----'}</td>
                                    <td>
                                        {addon.values?.map((v, i) => <div key={i}>{v.selectedValue}</div>)}
                                    </td>
                                    <td className="text-center">
                                        {addon.values?.map((v, i) => <div key={i}>{v.totalSaleCount}</div>)}
                                    </td>
                                    <td className="text-center text-success fw-semibold">{addon.totalAddonSalesCount}</td>
                                    <td className="text-end">₹ {(addon.totalDiscount || 0).toFixed(2)}</td>
                                    <td className="text-end">₹ {(addon.totalTaxable || 0).toFixed(2)}</td>
                                    <td className="text-end">₹ {(addon.totalTaxAmount || 0).toFixed(2)}</td>
                                    <td className="text-end fw-bold text-success">₹ {(addon.totalGrandTotal || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                            <tr className="fw-bold bg-light">
                                <td colSpan={6} className="text-end">Total:</td>
                                <td className="text-center text-success">{totals.totalAddonSalesCount}</td>
                                <td className="text-end">₹ {totals.totalDiscount.toFixed(2)}</td>
                                <td className="text-end">₹ {totals.totalTaxable.toFixed(2)}</td>
                                <td className="text-end">₹ {totals.totalTaxAmount.toFixed(2)}</td>
                                <td className="text-end text-success">₹ {totals.totalGrandTotal.toFixed(2)}</td>
                            </tr>
                        </>
                    ) : (
                        <tr>
                            <td colSpan={11} className="text-center py-4">No data available</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────────── */
const AddonSalesReport = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const animatedComponents = makeAnimated();
    const navigate = useNavigate();

    const accessToken = localStorage.getItem('authToken');

    // ── Filter state ─────────────────────────────────────────────────────────
    const [selectedOutlets, setSelectedOutlets] = useState([]);   // array for multi
    const [categoryList, setCategoryList] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]); // multi cat too
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState('');
    const [addonGroup, setAddonGroup] = useState(null);
    const [addons, setAddons] = useState([]);
    const [filteredAddonValues, setFilteredAddonValues] = useState([]);
    const [selectedAddonValue, setSelectedAddonValue] = useState(null);

    // ── Result state ─────────────────────────────────────────────────────────
    // rawData = array of outlet objects from API: [{ outlet, outletName, addons[], totals… }]
    const [rawData, setRawData] = useState([]);
    const [clicked, setClicked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    // ── View mode: 'cumulative' | 'outlet-wise' ───────────────────────────────
    const [viewMode, setViewMode] = useState('cumulative');
    // For outlet-wise: which outlet tab is active
    const [activeOutletId, setActiveOutletId] = useState(null);

    // ── Search (applied within the active view) ───────────────────────────────
    const [searchText, setSearchText] = useState('');
    const [searchApplied, setSearchApplied] = useState('');

    /* ── Derived data ─────────────────────────────────────────────────────── */

    // Cumulative: flatten all outlet addons into one list
    const cumulativeRows = useMemo(() =>
        rawData.flatMap(o => o.addons ?? []),
        [rawData]
    );

    const filteredCumulative = useMemo(() => {
        if (!searchApplied) return cumulativeRows;
        const q = searchApplied.toLowerCase();
        return cumulativeRows.filter(r => r.productName?.toLowerCase().includes(q));
    }, [cumulativeRows, searchApplied]);

    // Outlet-wise: rows for the active outlet tab
    const outletWiseRows = useMemo(() => {
        if (!activeOutletId) return [];
        const outlet = rawData.find(o => o.outlet === activeOutletId);
        if (!outlet) return [];
        if (!searchApplied) return outlet.addons ?? [];
        const q = searchApplied.toLowerCase();
        return (outlet.addons ?? []).filter(r => r.productName?.toLowerCase().includes(q));
    }, [rawData, activeOutletId, searchApplied]);

    /* ── API helpers ──────────────────────────────────────────────────────── */
    const buildQueryParams = (start, end, isDownload = false, isCosting = false) => {
        const s = start?.toISOString().split('T')[0];
        const e = end?.toISOString().split('T')[0];
        // let qs = `startDate=${s}&endDate=${e}&isDownload=${isDownload}`;
        let qs = `startDate=${s}&endDate=${e}`;
        if (addonGroup) qs += `&addonId=${addonGroup.value}`;
        if (selectedAddonValue) qs += `&addonValueId=${selectedAddonValue.value}`;
        if (isCosting) qs += `&isCosting=true`;
        return qs;
    };

    const buildBody = () => ({
        outletIds: selectedOutlets.map(o => o.value),
        categoryIds: selectedCategories.map(c => c.value),
    });

    const fetchReport = async (start, end) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const qs = buildQueryParams(start, end);
            const response = await axios.post(
                `addons/addon-sales-report-for-outlet/new?${qs}`,
                buildBody(),
                { headers }
            );
            const data = response?.data?.data ?? [];
            setRawData(data);
            // Default to first outlet tab if any
            if (data.length > 0) setActiveOutletId(data[0].outlet);
            toast.success('Data Fetched Successfully', { position: 'top-right', autoClose: 3000, theme: 'light' });
        } catch (err) {
            setRawData([]);
            handleApiError(err);
        }
    };

    const handleDownloadExcel = async (isCosting) => {
        setDownloadBtnLoading(true);
        try {
            const adjustedStart = new Date(startDate); adjustedStart.setHours(23, 59, 59, 999);
            const adjustedEnd = new Date(endDate); adjustedEnd.setHours(23, 59, 59, 999);

            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            };
            const qs = buildQueryParams(adjustedStart, adjustedEnd, true, isCosting);
            const response = await axios.post(
                `addons/addon-sales-report-for-outlet/new?${qs}`,
                buildBody(),
                { headers, responseType: 'blob' }
            );

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            const s = adjustedStart.toISOString().split('T')[0];
            const e = adjustedEnd.toISOString().split('T')[0];
            link.download = `Addon Sales Report (${s} - ${e}).xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);

            toast.success('Excel downloaded successfully!', { position: 'top-right', autoClose: 3000, theme: 'light' });
        } catch (err) {
            handleApiError(err);
        } finally {
            setDownloadBtnLoading(false);
        }
    };

    const handleApiError = (error) => {
        const s = error.response?.status;
        let msg = 'An error occurred';
        if ([406, 404, 412].includes(s))
            msg = error.response?.data?.error?.message || error.response?.data?.data?.message || msg;
        else if (s === 500) msg = error.response?.data?.error || msg;
        toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
    };

    /* ── Form handlers ────────────────────────────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setClicked(true);
        setBtnLoading(true);
        setSearchText('');
        setSearchApplied('');
        const adjustedStart = new Date(startDate); adjustedStart.setHours(23, 59, 59, 999);
        const adjustedEnd = new Date(endDate); adjustedEnd.setHours(23, 59, 59, 999);
        await fetchReport(adjustedStart, adjustedEnd);
        setBtnLoading(false);
    };

    const handleEndDateChange = (date) => {
        const start = new Date(startDate); start.setHours(0, 0, 0, 0);
        const end = new Date(date); end.setHours(0, 0, 0, 0);
        const maxEnd = new Date(start); maxEnd.setDate(start.getDate() + MAX_DATE_RANGE_DAYS);
        if (end < start) { setError('End date cannot be earlier than start date'); return; }
        setError(''); setEndDate(date);
    };

    const handleSearchProducts = () => setSearchApplied(searchText.trim());

    /* ── Init ─────────────────────────────────────────────────────────────── */
    useEffect(() => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        setStartDate(new Date(first.setHours(23, 59, 59, 999)));
        setEndDate(new Date(today.setHours(23, 59, 59, 999)));
        getCategoryList();
        getAddons();
    }, []);

    const getAddons = async () => {
        try {
            const response = await axios.get('addons?page=1&limit=1000', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setAddons(response?.data?.data?.addons ?? []);
        } catch { /* silent */ }
    };

    const getCategoryList = async () => {
        try {
            const response = await axios.get('masters/category/get', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (response?.data?.status) setCategoryList(response?.data?.data?.categories ?? []);
        } catch (err) { handleApiError(err); }
    };

    // Populate addon value options when group changes
    useEffect(() => {
        if (addonGroup) {
            const grp = addons.find(a => a._id === addonGroup.value);
            setFilteredAddonValues(
                (grp?.addonValues ?? []).map(v => ({ value: v._id, label: v.value }))
            );
        } else {
            setFilteredAddonValues([]);
            setSelectedAddonValue(null);
        }
    }, [addonGroup, addons]);

    /* ── Print ────────────────────────────────────────────────────────────── */
    const handlePrintReport = () => {
        const rowsToPrint = viewMode === 'cumulative' ? filteredCumulative : outletWiseRows;
        const totals = rowsToPrint.reduce((acc, r) => ({
            totalAddonSalesCount: acc.totalAddonSalesCount + (r.totalAddonSalesCount || 0),
            totalDiscount: acc.totalDiscount + (r.totalDiscount || 0),
            totalTaxable: acc.totalTaxable + (r.totalTaxable || 0),
            totalTaxAmount: acc.totalTaxAmount + (r.totalTaxAmount || 0),
            totalGrandTotal: acc.totalGrandTotal + (r.totalGrandTotal || 0),
        }), { totalAddonSalesCount: 0, totalDiscount: 0, totalTaxable: 0, totalTaxAmount: 0, totalGrandTotal: 0 });

        const printWindow = window.open('', '_blank');
        const viewLabel = viewMode === 'cumulative'
            ? 'Cumulative (All Outlets)'
            : rawData.find(o => o.outlet === activeOutletId)?.outletName ?? '';

        printWindow.document.write(`
            <html><head><title>Addon Sales Report</title>
            <style>
                body{font-family:Arial,sans-serif;margin:20px}
                .header{text-align:center;margin-bottom:20px}
                table{width:100%;border-collapse:collapse}
                th{background:#f8f9fa;padding:8px;border:1px solid #dee2e6;text-align:left}
                td{padding:8px;border:1px solid #dee2e6}
                .r{text-align:right}.c{text-align:center}.g{color:#198754}.b{font-weight:700}
                @page{size:auto;margin:10mm}
            </style></head><body>
            <div class="header">
                <h2>Addon Sales Report — ${viewLabel}</h2>
                <p>Generated: ${new Date().toLocaleString()}</p>
            </div>
            <table><thead><tr>
                <th>#</th><th>Outlet</th><th>Category &amp; Product</th><th>Addon Group</th>
                <th>Addon Values</th><th class="c">Sales Count</th><th class="c">Total Sales Count</th>
                <th class="r">Discount</th><th class="r">Taxable</th><th class="r">Tax</th><th class="r">Grand Total</th>
            </tr></thead><tbody>
            ${rowsToPrint.map((a, i) => `
                <tr>
                    <td>${i + 1}</td><td>${a.outletName || '--'}</td>
                    <td>${a.productName || '--'}<br/><b>${a.categoryName || ''}</b></td>
                    <td>${a.addonTitle || '----'}</td>
                    <td>${(a.values ?? []).map(v => v.selectedValue).join('<br/>')}</td>
                    <td class="c">${(a.values ?? []).map(v => v.totalSaleCount).join('<br/>')}</td>
                    <td class="c g">${a.totalAddonSalesCount || 0}</td>
                    <td class="r">₹ ${(a.totalDiscount || 0).toFixed(2)}</td>
                    <td class="r">₹ ${(a.totalTaxable || 0).toFixed(2)}</td>
                    <td class="r">₹ ${(a.totalTaxAmount || 0).toFixed(2)}</td>
                    <td class="r g b">₹ ${(a.totalGrandTotal || 0).toFixed(2)}</td>
                </tr>`).join('')}
            <tr class="b">
                <td colspan="6" class="r">Total:</td>
                <td class="c g">${totals.totalAddonSalesCount}</td>
                <td class="r">₹ ${totals.totalDiscount.toFixed(2)}</td>
                <td class="r">₹ ${totals.totalTaxable.toFixed(2)}</td>
                <td class="r">₹ ${totals.totalTaxAmount.toFixed(2)}</td>
                <td class="r g">₹ ${totals.totalGrandTotal.toFixed(2)}</td>
            </tr>
            </tbody></table>
            <script>window.onload=function(){setTimeout(function(){window.print();window.close();},200)}</script>
            </body></html>`);
        printWindow.document.close();
    };

    /* ── Derived for submit button ────────────────────────────────────────── */
    const canSubmit = startDate && endDate && selectedOutlets.length > 0 && !error;
    const hasData = rawData.length > 0;

    /* ─────────────────────────────────────────────────────────────────────── */
    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
                <div className="card shadow-sm border-0">
                    {/* ── Card Header ── */}
                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 fw-bold">
                            <i className="bi bi-sliders me-2"></i>ADDON SALES REPORT
                        </h4>
                        <Button
                            variant="outline-secondary" size="sm"
                            onClick={() => navigate('/reports/all-reports')}
                            className="d-flex align-items-center fw-semibold"
                            style={{ height: 38 }}
                        >
                            <ArrowLeft className="me-1" /> Back to Reports
                        </Button>
                    </div>
                    <hr className="mt-0 mb-0" />

                    <div className="card-body">
                        {/* ── Filters ── */}
                        <div className="p-3 bg-light rounded-3 mb-3">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    {/* Multi-select outlets */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <OutletSelect
                                                isMulti
                                                value={selectedOutlets}
                                                onChange={(opts) => setSelectedOutlets(opts ?? [])}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Multi-select categories */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-tags me-2"></i>Category
                                            </Form.Label>
                                            <Select
                                                isMulti
                                                options={categoryList.map(c => ({ value: c._id, label: c.name }))}
                                                value={selectedCategories}
                                                onChange={(opts) => setSelectedCategories(opts ?? [])}
                                                classNamePrefix="react-select"
                                                isClearable
                                                placeholder="All Categories"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Start date */}
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar me-2"></i>Start Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => {
                                                    setStartDate(date);
                                                    if (endDate && date && endDate > new Date(date.getTime() + MAX_DATE_RANGE_DAYS * 864e5))
                                                        setEndDate(null);
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* End date */}
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar me-2"></i>End Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => {
                                                    if (startDate && date > new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 864e5)) {
                                                        setError(DATE_RANGE_ERROR_MESSAGE);
                                                    } else {
                                                        setError('');
                                                        handleEndDateChange(date);
                                                    }
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                minDate={startDate}
                                                maxDate={startDate ? new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 864e5) : null}
                                                className="form-control border-secondary"
                                            />
                                            {error && <div className="text-danger small mt-1">{error}</div>}
                                        </Form.Group>
                                    </Col>

                                    {/* Submit */}
                                    <Col md={2}>
                                        <Form.Label className="text-white">.</Form.Label>
                                        <Button variant="primary" type="submit" className="w-100" disabled={!canSubmit}>
                                            {btnLoading ? <Spinner size="sm" animation="border" /> : 'Generate Report'}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {/* ── Addon filters + Search + Download ── */}
                        <div className="mb-3 p-3 bg-light rounded-3 pt-0">
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-box me-2"></i>Addon Group
                                    </Form.Label>
                                    <Select
                                        options={addons.map(a => ({ value: a._id, label: a.title }))}
                                        value={addonGroup}
                                        onChange={setAddonGroup}
                                        classNamePrefix="react-select"
                                        isClearable
                                        placeholder="Select Addon Group"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-collection me-2"></i>Addon Values
                                    </Form.Label>
                                    <Select
                                        components={animatedComponents}
                                        options={filteredAddonValues}
                                        value={selectedAddonValue}
                                        onChange={setSelectedAddonValue}
                                        classNamePrefix="select"
                                        placeholder="Select Addon Value"
                                        isClearable
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Label className="fw-semibold">Search</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by product name"
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleSearchProducts()}
                                            className="border-secondary"
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleSearchProducts}
                                            disabled={!searchText.trim()}
                                        >
                                            <Search className="me-1" /> Search
                                        </Button>
                                    </InputGroup>
                                </Col>

                                <Col md={2} className="d-flex gap-2 justify-content-end">
                                    <DropdownButton
                                        variant="outline-success"
                                        className="fw-semibold"
                                        title={
                                            <>
                                                <FileEarmarkSpreadsheet className="me-2" />
                                                {downloadBtnLoading ? <Spinner size="sm" /> : 'Download Excel'}
                                            </>
                                        }
                                        disabled={!hasData || downloadBtnLoading}
                                    >
                                        <Dropdown.Item onClick={() => handleDownloadExcel(false)}>
                                            <FileEarmarkSpreadsheet className="me-2 text-success" />
                                            Without Costing
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => handleDownloadExcel(true)}>
                                            <FileEarmarkSpreadsheet className="me-2 text-success" />
                                            With Costing
                                        </Dropdown.Item>
                                    </DropdownButton>

                                    {/* <Button
                                        variant="outline-secondary"
                                        onClick={handlePrintReport}
                                        disabled={!hasData}
                                        title="Print current view"
                                    >
                                        <i className="bi bi-printer me-1"></i> Print
                                    </Button> */}
                                </Col>
                            </Row>
                        </div>

                        {/* ── Results ── */}
                        {!clicked ? (
                            <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                                <div className="text-center">
                                    <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                                    <h5 className="text-muted mt-3">Please select filters and generate report</h5>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* ── View mode toggle + outlet summary badges ── */}
                                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                    {/* Toggle buttons */}
                                    <div className="btn-group" role="group">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${viewMode === 'cumulative' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setViewMode('cumulative')}
                                        >
                                            <i className="bi bi-bar-chart-line me-1"></i> Cumulative
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${viewMode === 'outlet-wise' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setViewMode('outlet-wise')}
                                            disabled={rawData.length === 0}
                                        >
                                            <i className="bi bi-diagram-3 me-1"></i> Outlet-wise
                                        </button>
                                    </div>

                                    {/* Outlet summary badges (cumulative view) */}
                                    {viewMode === 'cumulative' && rawData.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2">
                                            {rawData.map(o => (
                                                <Badge key={o.outlet} bg="light" text="dark" className="border px-2 py-1" style={{ fontSize: 12 }}>
                                                    <span className="text-primary fw-semibold">{o.outletName}</span>
                                                    <span className="ms-2 text-success">₹ {(o.totalGrandTotal || 0).toFixed(2)}</span>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* ── Outlet-wise tabs ── */}
                                {viewMode === 'outlet-wise' && rawData.length > 0 && (
                                    <Nav variant="tabs" className="mb-3" activeKey={activeOutletId} onSelect={setActiveOutletId}>
                                        {rawData.map(o => (
                                            <Nav.Item key={o.outlet}>
                                                <Nav.Link eventKey={o.outlet}>
                                                    {o.outletName}
                                                    <Badge bg="success" className="ms-2" style={{ fontSize: 11 }}>
                                                        ₹ {(o.totalGrandTotal || 0).toFixed(0)}
                                                    </Badge>
                                                </Nav.Link>
                                            </Nav.Item>
                                        ))}
                                    </Nav>
                                )}

                                {/* ── Summary cards (outlet-wise active outlet) ── */}
                                {viewMode === 'outlet-wise' && activeOutletId && (() => {
                                    const o = rawData.find(x => x.outlet === activeOutletId);
                                    if (!o) return null;
                                    return (
                                        <Row className="g-2 mb-3">
                                            {[
                                                { label: 'Total Sales Count', val: o.totalAddonSalesCount, icon: 'bi-bag-check' },
                                                { label: 'Discount', val: `₹ ${(o.totalDiscount || 0).toFixed(2)}`, icon: 'bi-tag' },
                                                { label: 'Taxable', val: `₹ ${(o.totalTaxable || 0).toFixed(2)}`, icon: 'bi-receipt' },
                                                { label: 'Tax', val: `₹ ${(o.totalTaxAmount || 0).toFixed(2)}`, icon: 'bi-percent' },
                                                { label: 'Grand Total', val: `₹ ${(o.totalGrandTotal || 0).toFixed(2)}`, icon: 'bi-cash-stack', highlight: true },
                                            ].map((card, i) => (
                                                <Col key={i} xs={6} md>
                                                    <div className={`border rounded-3 p-2 text-center ${card.highlight ? 'bg-success bg-opacity-10 border-success' : 'bg-white'}`}>
                                                        <i className={`bi ${card.icon} fs-5 ${card.highlight ? 'text-success' : 'text-muted'}`}></i>
                                                        <div className="small text-muted mt-1">{card.label}</div>
                                                        <div className={`fw-bold ${card.highlight ? 'text-success' : ''}`}>{card.val}</div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    );
                                })()}

                                {/* ── Summary cards (cumulative view) ── */}
                                {viewMode === 'cumulative' && rawData.length > 0 && (() => {
                                    const totals = rawData.reduce((acc, o) => ({
                                        totalAddonSalesCount: acc.totalAddonSalesCount + (o.totalAddonSalesCount || 0),
                                        totalDiscount: acc.totalDiscount + (o.totalDiscount || 0),
                                        totalTaxable: acc.totalTaxable + (o.totalTaxable || 0),
                                        totalTaxAmount: acc.totalTaxAmount + (o.totalTaxAmount || 0),
                                        totalGrandTotal: acc.totalGrandTotal + (o.totalGrandTotal || 0),
                                    }), { totalAddonSalesCount: 0, totalDiscount: 0, totalTaxable: 0, totalTaxAmount: 0, totalGrandTotal: 0 });
                                    return (
                                        <Row className="g-2 mb-3">
                                            {[
                                                { label: 'Total Sales Count', val: totals.totalAddonSalesCount, icon: 'bi-bag-check' },
                                                { label: 'Discount', val: `₹ ${totals.totalDiscount.toFixed(2)}`, icon: 'bi-tag' },
                                                { label: 'Taxable', val: `₹ ${totals.totalTaxable.toFixed(2)}`, icon: 'bi-receipt' },
                                                { label: 'Tax', val: `₹ ${totals.totalTaxAmount.toFixed(2)}`, icon: 'bi-percent' },
                                                { label: 'Grand Total', val: `₹ ${totals.totalGrandTotal.toFixed(2)}`, icon: 'bi-cash-stack', highlight: true },
                                            ].map((card, i) => (
                                                <Col key={i} xs={6} md>
                                                    <div className={`border rounded-3 p-2 text-center ${card.highlight ? 'bg-success bg-opacity-10 border-success' : 'bg-white'}`}>
                                                        <i className={`bi ${card.icon} fs-5 ${card.highlight ? 'text-success' : 'text-muted'}`}></i>
                                                        <div className="small text-muted mt-1">{card.label}</div>
                                                        <div className={`fw-bold ${card.highlight ? 'text-success' : ''}`}>{card.val}</div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    );
                                })()}

                                {/* ── The actual table ── */}
                                <AddonTable
                                    rows={viewMode === 'cumulative' ? filteredCumulative : outletWiseRows}
                                    loading={loading}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default AddonSalesReport;